/**
 * The bytes behind an attachment — decision 54.
 *
 * The record in Firestore holds name, size and the date it was filed; the file
 * itself lives in Storage under `ownership/{scope}/{recordId}/{attachmentId}`.
 *
 * ## Uploaded immediately, not on save
 *
 * Picking a file uploads it there and then, and the form holds the resulting
 * metadata. The alternative — hold the `File` objects and upload them when the
 * modal saves — reads tidier and is worse in the two ways that matter: a save
 * would sometimes take thirty seconds with no way to show progress per file,
 * and a failure halfway would leave a record saved with some of its documents
 * missing and no record of which.
 *
 * The cost is an orphan: upload a file, then close the modal without saving,
 * and the bytes sit in the bucket with nothing pointing at them. That is the
 * right way round. An orphaned object is invisible and cheap; a shareholder
 * whose firmaattest silently failed to upload is a share register that lies.
 */
import { getStorage, ref, uploadBytes, deleteObject, getDownloadURL } from 'firebase/storage'
import { app } from '@/shared/firebase/client'
import { todayIso } from '@/shared/util/dates'
import type { Attachment } from '@/types/ownership'

export type AttachmentScope = 'shareholders' | 'issuances'

/**
 * A new record has no id until it is saved, so its attachments go to a draft
 * folder and are moved by nothing — the path is stored on the attachment, so
 * where the bytes sit never has to be inferred from the record they end up on.
 */
const DRAFT = 'unsaved'

/** Enough entropy to never collide, short enough to read in a bucket listing. */
const newId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

export async function uploadAttachment(
  scope: AttachmentScope,
  recordId: string | undefined,
  file: File,
): Promise<Attachment> {
  const id = newId()
  const path = `ownership/${scope}/${recordId || DRAFT}/${id}`
  await uploadBytes(ref(getStorage(app), path), file, {
    // The filename is not recoverable from the path — the path carries the id
    // so two records can file the same name — so it travels as metadata too.
    // Storage is then readable on its own terms if the register is ever lost.
    customMetadata: { filename: file.name },
  })
  return { id, name: file.name, size: file.size, uploadedAt: todayIso(), path }
}

/**
 * Removing a file removes the bytes as well.
 *
 * A missing object is not an error here: the record is the thing being
 * corrected, and refusing to drop a row because its file was already gone would
 * leave the register permanently unable to tidy itself.
 */
export async function deleteAttachment(attachment: Attachment): Promise<void> {
  try {
    await deleteObject(ref(getStorage(app), attachment.path))
  } catch {
    // Already gone, or never uploaded. Either way the row comes off.
  }
}

/** A short-lived URL for opening a filed document. */
export async function attachmentUrl(attachment: Attachment): Promise<string> {
  return getDownloadURL(ref(getStorage(app), attachment.path))
}
